import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { ChevronLeft, ChevronRight, Waves, Utensils, Dumbbell, Calendar, Users, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const HomePage = () => {
  const { t, currentLanguage } = useLanguage();
  const { settings } = useSettings();
  
  const [sliders, setSliders] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Reservation form state
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  const fallbackSlides = [
    {
      url: 'https://customer-assets.emergentagent.com/job_bodrum-resort/artifacts/ik5mme3p_RES%C4%B0M%202.jpg',
      titles: { tr: "Ege'nin En Yeni İncisi", en: "The Newest Pearl of the Aegean", de: "Die neueste Perle der Ägäis", ru: "Новая жемчужина Эгейского моря" },
      subtitles: { tr: "Ultra Her Şey Dahil & Aile Dostu", en: "Ultra All-Inclusive & Family Friendly", de: "Ultra All-Inclusive & Familienfreundlich", ru: "Ультра Все Включено и для Семей" }
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_bodrum-resort/artifacts/01x2aofs_RES%C4%B0M%203.jpg',
      titles: { tr: "Eşsiz Manzara", en: "Unique View", de: "Einzigartige Aussicht", ru: "Уникальный вид" },
      subtitles: { tr: "Kos ve Kalymnos Adalarına Hakim", en: "Overlooking Kos and Kalymnos Islands", de: "Mit Blick auf die Inseln Kos und Kalymnos", ru: "С видом на острова Кос и Калимнос" }
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_bodrum-resort/artifacts/jy7bwzmx_RES%C4%B0M%204.jpg',
      titles: { tr: "Lüks Konaklama", en: "Luxury Accommodation", de: "Luxuriöse Unterkunft", ru: "Роскошное размещение" },
      subtitles: { tr: "460 Oda ile Konfor", en: "Comfort with 460 Rooms", de: "Komfort mit 460 Zimmern", ru: "Комфорт с 460 номерами" }
    }
  ];

  useEffect(() => {
    const fetchSliders = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/slider`);
        if (response.data && response.data.length > 0) {
          setSliders(response.data);
        }
      } catch (error) {
        console.error('Error fetching sliders:', error);
      }
    };
    fetchSliders();
  }, []);

  const activeSlides = sliders.length > 0 ? sliders : fallbackSlides;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [activeSlides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + activeSlides.length) % activeSlides.length);

  // Set default dates
  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(today);
    dayAfter.setDate(dayAfter.getDate() + 2);
    
    setCheckIn(tomorrow.toISOString().split('T')[0]);
    setCheckOut(dayAfter.toISOString().split('T')[0]);
  }, []);

  const handleReservation = () => {
    const baseUrl = settings.reservation_url || 'https://www.ihg.com';
    let targetUrl = baseUrl;
    
    if (baseUrl.includes('ihg.com')) {
      targetUrl = `https://www.ihg.com/holidayinn/hotels/tr/tr/find-hotels/hotel/rooms?qDest=Bodrum&qCiD=${checkIn}&qCoD=${checkOut}&qAdlt=${adults}&qChld=${children}&qRms=1`;
    }
    window.open(targetUrl, '_blank');
  };

  const reservationLabels = {
    tr: { checkIn: 'Giriş', checkOut: 'Çıkış', guests: 'Misafir', search: 'Ara' },
    en: { checkIn: 'Check-in', checkOut: 'Check-out', guests: 'Guests', search: 'Search' },
    de: { checkIn: 'Anreise', checkOut: 'Abreise', guests: 'Gäste', search: 'Suchen' },
    ru: { checkIn: 'Заезд', checkOut: 'Выезд', guests: 'Гости', search: 'Поиск' }
  };

  const labels = reservationLabels[currentLanguage] || reservationLabels.tr;

  const highlights = [
    {
      icon: Waves,
      title: t('pool.title'),
      description: t('pool.subtitle'),
      image: '/images/general/general_3.jpg',
      link: '/pool'
    },
    {
      icon: Utensils,
      title: t('dining.title'),
      description: t('dining.subtitle'),
      image: '/images/dining/main_restaurant.jpg',
      link: '/dining'
    },
    {
      icon: Dumbbell,
      title: t('spa.title'),
      description: t('spa.subtitle'),
      image: '/images/general/general_2.jpg',
      link: '/spa'
    },
  ];

  const roomTypes = [
    {
      title: t('rooms.premium.title'),
      size: t('rooms.premium.size'),
      image: '/images/rooms/premium_garden.jpg',
      description: t('rooms.premium.description'),
    },
    {
      title: t('rooms.family.title'),
      size: t('rooms.family.size'),
      image: '/images/rooms/family_garden.jpg',
      description: t('rooms.family.description'),
    },
    {
      title: t('rooms.queenSuite.title'),
      size: t('rooms.queenSuite.size'),
      image: '/images/rooms/queen_suite.jpg',
      description: t('rooms.queenSuite.description'),
    },
  ];

  const getLocalizedText = (obj) => {
    if (!obj) return '';
    return obj[currentLanguage] || obj['tr'] || obj['en'] || '';
  };

  const addressText = settings.address?.[currentLanguage] || settings.address?.tr || t('location.addressValue');
  const mapEmbed = settings.google_maps_embed || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d50324.55766594!2d27.2196!3d37.0267!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14be6b70f5555555%3A0x5555555555555555!2sTurgutreis%2C%20Bodrum!5e0!3m2!1sen!2str!4v1234567890';
  const hotelName = settings.hotel_name || 'Holiday Inn Resort Bodrum West Beach';

  return (
    <div data-testid="home-page" className="pb-16 md:pb-0">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[500px] md:min-h-[700px] overflow-hidden" data-testid="hero-section">
        {activeSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.url}
              alt={`Holiday Inn Resort slide ${index + 1}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4 pt-16">
          <div className="animate-fade-in-up max-w-4xl mx-auto mb-6 md:mb-12">
            <p className="text-xs md:text-sm tracking-[0.25em] uppercase mb-2 md:mb-4 text-white/90 font-medium">
              {t('hero.location')}
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-3 md:mb-5 leading-tight drop-shadow-lg">
              {getLocalizedText(activeSlides[currentSlide]?.titles) || t('hero.title')}
            </h1>
            <p className="text-sm md:text-lg lg:text-xl text-white/95 mb-1 md:mb-2 drop-shadow">
              {getLocalizedText(activeSlides[currentSlide]?.subtitles) || t('hero.subtitle')}
            </p>
            <p className="text-xs md:text-base text-white/80">
              {t('hero.concept')}
            </p>
          </div>
        </div>

        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full items-center justify-center transition-colors backdrop-blur-sm"
          data-testid="hero-prev"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={nextSlide}
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full items-center justify-center transition-colors backdrop-blur-sm"
          data-testid="hero-next"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-4 md:bottom-[130px] left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {activeSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide ? 'bg-white w-6' : 'bg-white/50'
              }`}
              data-testid={`hero-indicator-${index}`}
            />
          ))}
        </div>

        {/* Desktop Reservation Widget */}
        <div className="hidden md:block absolute bottom-0 left-0 right-0 z-20" data-testid="reservation-widget-desktop">
          <div className="container-custom px-4">
            <div className="bg-white rounded-t-xl shadow-2xl">
              <div className="flex items-center divide-x divide-gray-200">
                {/* Check-in */}
                <div className="flex-1 p-4 lg:p-5">
                  <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">{labels.checkIn}</label>
                  <div className="relative">
                    <Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-holiday-purple" />
                    <input
                      type="date"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      className="w-full pl-7 py-1 text-base font-medium text-gray-900 border-0 focus:ring-0 cursor-pointer"
                      data-testid="checkin-desktop"
                    />
                  </div>
                </div>

                {/* Check-out */}
                <div className="flex-1 p-4 lg:p-5">
                  <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">{labels.checkOut}</label>
                  <div className="relative">
                    <Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-holiday-purple" />
                    <input
                      type="date"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      className="w-full pl-7 py-1 text-base font-medium text-gray-900 border-0 focus:ring-0 cursor-pointer"
                      data-testid="checkout-desktop"
                    />
                  </div>
                </div>

                {/* Adults */}
                <div className="w-28 lg:w-32 p-4 lg:p-5">
                  <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">Yetişkin</label>
                  <div className="relative">
                    <Users className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-holiday-purple" />
                    <select
                      value={adults}
                      onChange={(e) => setAdults(parseInt(e.target.value))}
                      className="w-full pl-7 py-1 text-base font-medium text-gray-900 border-0 focus:ring-0 cursor-pointer appearance-none bg-transparent"
                      data-testid="adults-desktop"
                    >
                      {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                {/* Children */}
                <div className="w-28 lg:w-32 p-4 lg:p-5">
                  <label className="block text-xs text-gray-500 mb-1 uppercase tracking-wider">Çocuk</label>
                  <div className="relative">
                    <Users className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-holiday-green" />
                    <select
                      value={children}
                      onChange={(e) => setChildren(parseInt(e.target.value))}
                      className="w-full pl-7 py-1 text-base font-medium text-gray-900 border-0 focus:ring-0 cursor-pointer appearance-none bg-transparent"
                      data-testid="children-desktop"
                    >
                      {[0,1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                {/* Search Button */}
                <div className="p-4 lg:p-5">
                  <button
                    onClick={handleReservation}
                    className="bg-holiday-green hover:bg-holiday-green-dark text-white px-8 lg:px-10 py-3.5 rounded font-semibold flex items-center gap-2 transition-colors whitespace-nowrap"
                    data-testid="reservation-submit-desktop"
                  >
                    <Search className="w-5 h-5" />
                    {labels.search}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Fixed Bottom Reservation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.15)]" data-testid="reservation-widget-mobile">
        <div className="flex items-center h-14 px-2">
          {/* Check-in */}
          <div className="flex-1 px-1">
            <input
              type="date"
              value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
              className="w-full text-xs py-1.5 px-2 border border-gray-200 rounded bg-gray-50 focus:outline-none focus:border-holiday-purple"
              data-testid="checkin-mobile"
            />
          </div>
          
          {/* Check-out */}
          <div className="flex-1 px-1">
            <input
              type="date"
              value={checkOut}
              onChange={(e) => setCheckOut(e.target.value)}
              className="w-full text-xs py-1.5 px-2 border border-gray-200 rounded bg-gray-50 focus:outline-none focus:border-holiday-purple"
              data-testid="checkout-mobile"
            />
          </div>
          
          {/* Guests */}
          <div className="w-14 px-1">
            <select
              value={adults + children}
              onChange={(e) => setAdults(parseInt(e.target.value))}
              className="w-full text-xs py-1.5 px-1 border border-gray-200 rounded bg-gray-50 appearance-none text-center focus:outline-none focus:border-holiday-purple"
              data-testid="guests-mobile"
            >
              {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n} 👤</option>)}
            </select>
          </div>
          
          {/* Search Button */}
          <button
            onClick={handleReservation}
            className="bg-holiday-green hover:bg-holiday-green-dark text-white px-4 py-2 rounded font-semibold text-sm flex items-center gap-1 transition-colors ml-1"
            data-testid="reservation-submit-mobile"
          >
            <Search className="w-4 h-4" />
            {labels.search}
          </button>
        </div>
      </div>

      {/* Welcome Section */}
      <section className="section-padding bg-white" data-testid="welcome-section">
        <div className="container-custom px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-holiday-purple text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4">
              {hotelName}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-4 md:mb-6">
              {t('about.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed">
              {t('about.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="section-padding bg-gray-50" data-testid="highlights-section">
        <div className="container-custom px-4">
          <div className="text-center mb-10 md:mb-16">
            <p className="text-holiday-green text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4">
              {t('common.learnMore')}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900">
              Öne Çıkan Hizmetler
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {highlights.map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className="group card-elegant overflow-hidden"
                data-testid={`highlight-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-holiday-purple/10 rounded-full flex items-center justify-center mb-3 md:mb-4">
                    <item.icon className="w-5 h-5 md:w-6 md:h-6 text-holiday-purple" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-heading font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base">{item.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms Preview Section */}
      <section className="section-padding bg-white" data-testid="rooms-preview-section">
        <div className="container-custom px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-16">
            <div>
              <p className="text-holiday-purple text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4">
                {t('rooms.subtitle')}
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900">
                {t('rooms.title')}
              </h2>
            </div>
            <Link
              to="/rooms"
              className="mt-4 md:mt-0 text-holiday-purple hover:text-holiday-purple-dark font-medium flex items-center gap-2 text-sm md:text-base"
              data-testid="view-all-rooms"
            >
              {t('common.viewAll')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {roomTypes.map((room, index) => (
              <div
                key={index}
                className="group card-elegant overflow-hidden"
                data-testid={`room-card-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={room.image}
                    alt={room.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <h3 className="text-lg md:text-xl font-heading font-semibold text-gray-900">
                      {room.title}
                    </h3>
                    <span className="text-xs md:text-sm text-holiday-purple font-medium bg-holiday-purple/10 px-2 md:px-3 py-1 rounded-full">
                      {room.size}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{room.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative py-20 md:py-32 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `url('/images/general/hero_bg.jpg')`
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-holiday-purple/80" />
        <div className="relative z-10 container-custom px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4 md:mb-6">
            {t('hero.concept')}
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-6 md:mb-8 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>
          <button
            onClick={handleReservation}
            className="btn-accent inline-block rounded text-base md:text-lg px-8 md:px-10 py-3 md:py-4"
            data-testid="cta-book-now"
          >
            {t('nav.bookNow')}
          </button>
        </div>
      </section>

      {/* Location Preview */}
      <section className="section-padding bg-gray-50" data-testid="location-preview">
        <div className="container-custom px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <p className="text-holiday-green text-xs md:text-sm tracking-[0.3em] uppercase mb-3 md:mb-4">
                {t('location.subtitle')}
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-4 md:mb-6">
                {t('location.title')}
              </h2>
              <div className="space-y-4 md:space-y-6">
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">{t('location.address')}</h4>
                  <p className="text-gray-600 text-sm md:text-base">{addressText}</p>
                </div>
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">{t('location.view')}</h4>
                  <p className="text-gray-600 text-sm md:text-base">{t('location.viewValue')}</p>
                </div>
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">{t('location.highlights')}</h4>
                  <p className="text-gray-600 text-sm md:text-base">{t('location.highlightsValue')}</p>
                </div>
              </div>
              <Link
                to="/contact"
                className="btn-primary inline-block mt-6 md:mt-8 rounded text-sm md:text-base"
                data-testid="location-contact-btn"
              >
                {t('contact.form.title')}
              </Link>
            </div>
            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl">
              <iframe
                src={mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Holiday Inn Resort Bodrum West Beach Location"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
